import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi, type Person } from '@/lib/adminApi';
import { Card, DataTable, Spinner, EmptyState, Badge, type Column } from '@/components/ui';
import { IconCar } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader, SearchBox, AccountStatusBadge } from '@/components/admin/shared';
import { personName, dateTime } from '@/lib/adminFormat';
import { Pager } from '@/components/admin/Pager';
import { DriverDrawer } from '@/components/admin/DriverDrawer';

const PAGE = 25;

/** Drivers — list/search auth_users (scope=driver); a "Review" button opens a
 *  drawer with the driver detail, vehicle, the document-review approve/reject
 *  controls, and the earnings wallet. Covers the Filament DriverResource +
 *  DriverVehicleResource + the document-review actions. */
export function Drivers() {
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['admin', 'drivers', q, offset],
    queryFn: ({ signal }) => adminApi.drivers({ q, limit: PAGE, offset }, signal),
    placeholderData: (prev) => prev,
  });

  const columns: Array<Column<Person>> = [
    { key: 'id', header: 'ID', cell: (r) => <span className="metric text-xs text-muted">#{r.id}</span> },
    { key: 'name', header: 'Name', cell: (r) => <span className="font-medium text-slate-100">{personName(r)}</span> },
    { key: 'phone', header: 'Phone', cell: (r) => <span className="text-slate-300">{r.country_code}{r.phone}</span> },
    { key: 'email', header: 'Email', cell: (r) => <span className="text-slate-300">{r.email || '—'}</span> },
    {
      key: 'type',
      header: 'Type',
      cell: (r) =>
        r.service_line ? (
          <Badge tone="info">{r.service_line.charAt(0).toUpperCase() + r.service_line.slice(1)}</Badge>
        ) : (
          <span className="text-xs text-text-muted">—</span>
        ),
    },
    { key: 'status', header: 'Status', cell: (r) => <AccountStatusBadge status={r.status} /> },
    { key: 'joined', header: 'Joined', cell: (r) => <span className="text-xs text-muted">{dateTime(r.created_at)}</span> },
    {
      key: 'view',
      header: '',
      align: 'right',
      cell: (r) => (
        <button
          type="button"
          onClick={() => setSelected(r.id)}
          className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
        >
          Review
        </button>
      ),
    },
  ];

  return (
    <AdminPage>
      <AdminPageHeader
        title="Drivers"
        subtitle="Drivers — search, then review vehicle + documents."
        right={
          <SearchBox
            value={q}
            onChange={(v) => {
              setQ(v);
              setOffset(0);
            }}
            placeholder="Search drivers…"
          />
        }
      />

      <Card flush>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner label="Loading drivers…" />
          </div>
        ) : isError ? (
          <EmptyState title="Couldn't load drivers" description={(error as Error)?.message} />
        ) : (
          <>
            <DataTable<Person>
              columns={columns}
              rows={data?.items ?? []}
              rowKey={(r) => String(r.id)}
              stickyHeader
              empty={
                <EmptyState
                  title="No drivers"
                  description="No auth_users rows for scope=driver match this search. (Backfill may be pending.)"
                  icon={<IconCar className="h-6 w-6" />}
                />
              }
            />
            <Pager total={data?.total ?? 0} offset={offset} pageSize={PAGE} onOffset={setOffset} fetching={isFetching} />
          </>
        )}
      </Card>

      {selected !== null ? <DriverDrawer id={selected} onClose={() => setSelected(null)} /> : null}
    </AdminPage>
  );
}
